import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Button, { Props as ButtonProps } from 'mf-react-ui/Button';
import { EventId, trackEventBound, EventGTM, EventCat, EventName } from '../../../utils/ga';

export type Props = {
  /**
   * Управление `open/close` состоянием компонента. Позволяет задать изначально
   * развернутый `_draft_Dropdown`. Также позволяет задать значение по умолчанию
   */
  isOpen?: boolean;

  /** Свойства кнопки переключения */
  // @ts-ignore
  toggle?: ButtonProps;

  /** Содержимое кнопки переключения */
  toggleContent?: React.ReactNode;

  /** Обработчик смены состояний `open/close` */
  onToggle?: (isOpen: boolean) => void;

  /** Содержимое _draft_Dropdown */
  children: React.ReactNode;

  /** Дополнительный класс */
  className?: string;
};

const Dropdown: React.FC<Props> = ({
  isOpen = false,
  toggle,
  toggleContent,
  onToggle,
  children,
  className,
  ...attr
}) => {
  const [isOpenState, setIsOpenState] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const prevIsOpenStateRef = useRef(false);

  const handleToggle = () => {
    setIsOpenState(!isOpenState);

    if (!isOpenState) {
      trackEventBound(
        EventGTM.UseNavigations,
        EventId.ClickBurgerMenu,
        EventCat.GiftMobile,
        EventName.OpenMenu,
        '(empty)'
      );
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      e.target instanceof Element &&
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target)
    ) {
      setIsOpenState(false);

      if (onToggle) {
        onToggle(false);
      }
    }
  };

  const handleDropdownItemClick = (e: MouseEvent) => {
    if (
      e.target instanceof Element &&
      dropdownRef.current &&
      dropdownRef.current.contains(e.target) &&
      e.target.tagName === 'A'
    ) {
      setIsOpenState(false);

      if (onToggle) {
        onToggle(false);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('click', handleDropdownItemClick);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('click', handleDropdownItemClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen !== isOpenState) {
      setIsOpenState(isOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (prevIsOpenStateRef.current !== isOpenState && onToggle) {
      onToggle(isOpenState);
    }

    prevIsOpenStateRef.current = isOpenState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenState]);

  const dropdownProps = {
    className: classNames('dropdown', isOpenState && 'dropdown_opened', className),
    ...attr,
  };

  return (
    <div ref={dropdownRef} {...dropdownProps}>
      <Button
        view="clear"
        className="dropdown__handle"
        aria-haspopup="true"
        aria-expanded={isOpenState}
        onClick={handleToggle}
        {...toggle}
      >
        {toggleContent}
      </Button>
      <div className="dropdown__popup" aria-hidden={!isOpenState}>
        {children}
      </div>
    </div>
  );
};

export default Dropdown;
